import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

/**
 * TestimonialCarousel – zeigt alle veröffentlichten Bewertungen rotierend an.
 * - Desktop (md+): 3 Karten gleichzeitig, wechselt zur nächsten Gruppe
 * - Mobil: 1 Karte, wechselt einzeln
 * - Auto-Rotation alle ~6.5s, pausiert bei Hover / bei Fokus (a11y)
 * - Wenn die Anzahl der Bewertungen ≤ Slots pro View: keine Rotation, statische Anzeige
 * - Punkt-Navigation unter den Karten (klickbar)
 */
export default function TestimonialCarousel({ reviews }) {
  const [slotsPerView, setSlotsPerView] = useState(3);
  const [pageIndex, setPageIndex] = useState(0);
  const pausedRef = useRef(false);

  // Responsive: 1 Slot auf < md
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => setSlotsPerView(mq.matches ? 3 : 1);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Pages berechnen (bei 5 Reviews / 3 Slots → 2 Pages: [0,1,2] und [3,4,0])
  const pages = useMemo(() => {
    if (!reviews.length) return [];
    const total = reviews.length;
    const pageCount = Math.max(1, Math.ceil(total / slotsPerView));
    const out = [];
    for (let p = 0; p < pageCount; p++) {
      const slots = [];
      for (let s = 0; s < slotsPerView; s++) {
        slots.push(reviews[(p * slotsPerView + s) % total]);
      }
      out.push(slots);
    }
    return out;
  }, [reviews, slotsPerView]);

  // Aktueller pageIndex einschränken wenn slots wechseln
  useEffect(() => {
    if (pageIndex >= pages.length) setPageIndex(0);
  }, [pages.length, pageIndex]);

  // Auto-Rotation
  useEffect(() => {
    if (pages.length <= 1) return;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      setPageIndex((p) => (p + 1) % pages.length);
    }, 6500);
    return () => clearInterval(id);
  }, [pages.length]);

  if (!reviews.length) return null;
  const currentSlots = pages[pageIndex] || [];

  return (
    <div
      className="mt-10"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocus={() => { pausedRef.current = true; }}
      onBlur={() => { pausedRef.current = false; }}
      data-testid="home-testimonials-carousel"
    >
      <div className="relative min-h-[280px] md:min-h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={pageIndex}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="grid gap-6 md:grid-cols-3"
          >
            {currentSlots.map((r, i) => (
              <ReviewCard key={`${pageIndex}-${r.id}-${i}`} review={r} index={i} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {pages.length > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2" role="tablist" aria-label="Kundenstimmen navigieren">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => setPageIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === pageIndex ? "w-8 bg-yellow" : "w-1.5 bg-light-gray/40 hover:bg-light-gray/70"
              }`}
              aria-label={`Bewertungsgruppe ${i + 1}`}
              aria-selected={i === pageIndex}
              role="tab"
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ review, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className="flex h-full flex-col rounded-sm border border-dark-gray bg-brand-black p-8"
      data-testid={`home-review-${review.id}`}
    >
      <Quote className="h-8 w-8 text-yellow/70" aria-hidden="true" />
      {review.stars ? (
        <div className="mt-3 flex gap-0.5" aria-label={`${review.stars} von 5 Sternen`}>
          {Array.from({ length: 5 }).map((_, s) => (
            <Star
              key={s}
              className={`h-4 w-4 ${s < review.stars ? "fill-yellow text-yellow" : "text-dark-gray"}`}
            />
          ))}
        </div>
      ) : null}
      <p className="mt-4 flex-1 text-base leading-relaxed text-light-gray/90">„{review.text}“</p>
      <div className="mt-6 text-sm">
        {review.author && <span className="font-semibold text-white">{review.author}</span>}
        {review.source && <span className="ml-2 text-light-gray/50">· {review.source}</span>}
      </div>
    </motion.div>
  );
}
